import React, { useContext } from "react";
import {
  Search,
  // Command as CommandIcon,
  Home,
  AlertTriangle,
  Settings,
  LogOut,
  Clock2Icon,
  Sun,
  Moon,
  Monitor,
  Smartphone,
  User,
  Files,
  Loader,
  ChartArea,
  LayoutDashboard,
  StopCircle,
  Mic,
  File,
  Eraser,
  Expand,
  Shrink,
} from "lucide-react";
import { Link, useNavigate, useLocation, NavLink } from "react-router-dom";
import { Badge } from "@components/ui/badge";
import { GeneralContext } from "@services/providers/GeneralProvider";
import { Input } from "@components/ui/input";
import { Button } from "@components/ui/button";
import { Separator } from "@components/ui/separator";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@components/ui/popover";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  // DialogFooter,
  DialogHeader,
  DialogTitle,
  // DialogTrigger,
} from "@components/ui/dialog";
// import EntityInspector from "@pages/homepage/ddr-asset-auto-discovery/EntityInspector";
import { AssetEntityInspectorContent } from "@pages/homepage/asset-auto-discovery/AssetEntityInspector";
// import DDRUserInvestigator from "@components/common/ddr/user/DDRUserInvestigator";
import UserInvestigator from "@components/common/UserInvestigator";
import FileInvestigator from "@components/common/ddr/FileInvestigator";
import Notification from "./Notification";
import { useQuery, useMutation } from "react-query";
import { EpochToDateTime } from "@utils/utils";
import CustomTooltip from "./CustomTooltip";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@components/ui/tabs";
import {
  recentAlerts,
  readRecentAlerts,
  readSystemAlerts,
  setUpWizard,
  gethostlist,
  getAutomationApprovalAlerts,
  getShadowGPTTools,
} from "@services/api/home";
import {
  getSearchResults,
  fetchDeviceProperties,
  getSystemArchitectureInfo,
  fetchSelectedLanguage,
} from "@services/api/commonApis";
import { ChatMessage, HeaderProps } from "./CommonTypes";
import MapWheel from "./MapWheel";
import MapWheelCMX from "./MapWheelCMX";
import { useDebounce } from "@hooks/useDebounce";
import ShadowGPT from "./ShadowGPT";
import DataTable from "./DataTable";
import TwoFactorAuth from "./TwoFactorAuth";
import SetupWizard from "./SetupWizard";
import MsspView from "./MsspView";
import ConfirmDialog from "./ConfirmDialog";
import { fetchBasicSettings } from "@services/api/home";
import { isFeatureAllowed, ALL_KEYS } from "@utils/licenseUtils";
import { toast } from "@components/ui/use-toast";
import ReportStatusPopup from "./ReportStatusPopup";
import IncidentOverview from "./IncidentOverview";
import ApprovalPopup from "./ApprovalPopup";
import { Switch } from "@components/ui/switch";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "../ui/tooltip";
import { useTranslation } from "react-i18next";

const Header: React.FC<HeaderProps> = ({
  liveStatus = false,
  setLiveStatus = () => {},
}) => {
  const { t } = useTranslation();
  const [chatExpanded, setChatExpanded] = React.useState(false);
  const scale = window.innerWidth / 1920;
  const navigate = useNavigate();
  const shadowSearchRef = React.useRef<HTMLInputElement>(null);
  const location = useLocation();

  const shadowGPTStatus = sessionStorage.getItem("shadowGPTStatus") || "";

  const { data: shadowGPTdata } = useQuery(["shadowGPTTools"], () =>
    getShadowGPTTools(),
  );
  //Check if setup wizard is completed
  const { data: setupData } = useQuery(["setUpWizard"], () => setUpWizard());

  // ─────────────── Voice Search state & refs ───────────────
  const [isListening, setIsListening] = React.useState(false);
  const [isToolChecked, setIsToolChecked] = React.useState(false);
  const recognitionRef = React.useRef<any>(null);

  React.useEffect(() => {
    const SR =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;
    if (!SR) {
      console.warn("SpeechRecognition not supported");
      return;
    }
    try {
      const recog: any = new SR();
      recog.lang = "en-US";
      recog.interimResults = false;
      recognitionRef.current = recog;
    } catch (err) {
      console.error("SpeechRecognition init error", err);
    }
  }, []);

  const handleVoiceSearch = () => {
    const recog = recognitionRef.current;
    if (!recog) return;

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then(() => {
        if (!isListening) {
          setSearchOpen(true);
          setIsListening(true);
          recog.start();
        } else {
          setIsListening(false);
          recog.stop();
        }

        recog.onresult = (event: any) => {
          const speechResult = event.results[0][0].transcript;
          if (shadowSearchRef.current) {
            shadowSearchRef.current.value = speechResult;
          }
          setGlobalSearch(speechResult);
          setIsListening(false);
          setShadowSearch(speechResult);
          recog.stop();
        };

        recog.onspeechend = () => {
          recog.stop();
          setIsListening(false);
        };
        recog.onerror = (err: any) => {
          console.error("SpeechRecognition error", err.error);
          setIsListening(false);
          recog.stop();
        };
      })
      .catch(() => {
        toast({
          title: "Error",
          description:
            "Please allow microphone access in your browser settings.",
          variant: "destructive",
        });
      });
  };

  // const [searchVisible, setSearchVisible] = React.useState<boolean>(false);
  const [macAddress, setMacAddress] = React.useState<string>("");
  const [showLogoutConfirmation, setShowLogoutConfirmation] =
    React.useState(false);
  const [UserInvestigatorAttrs, setUserInvestigatorAttrs] = React.useState<any>(
    {},
  );
  const [fileId, setFileId] = React.useState(null);
  const [systemAlerts, setSystemAlerts] = React.useState<any[]>([]);
  const [recentAlert, setRecentAlert] = React.useState([]);
  const [recentDemoAlert, setRecentDemoAlert] = React.useState([]);
  const [notificationOpen, setNotificationOpen] = React.useState(false);

  const [systemAlertsRead, setSystemAlertsRead] = React.useState(false);
  const [recentAlertRead, setRecentAlertRead] = React.useState(false);
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [searchView, setSearchView] = React.useState(false);
  const [globalSearch, setGlobalSearch] = React.useState("");
  const [shadowSearch, setShadowSearch] = React.useState("");
  const [shadowSearchStop, setShadowSearchStop] = React.useState(false);
  const [advancedSearchLoading, setAdvancedSearchLoading] =
    React.useState(false);
  const [widgetTableRows, setWidgetTableRows] = React.useState([]);
  const [dashboardSearch, setDashboardSearch] = React.useState("");
  const [incidentOverviewProps, setIncidentOverviewProps] = React.useState({
    showIncidentOverview: false,
    incidentProps: {} as any,
  });

  let searchEventSource: any = null;
  // const [resultView, setResultView] = React.useState(false);
  const [globalSearchResult, setGlobalSearchResult] = React.useState<{
    entities: any;
    users: any;
    files: any;
  }>();
  const { theme, toggleTheme, productType } = useContext(GeneralContext);
  const normalizedProductType = (productType || "").trim().toLowerCase();
  const anomalyRouteBase =
    normalizedProductType === "ddr"
      ? "/risk-compliance"
      : "/aml-anomaly-detection";
  const [mapVisible, setMapVisible] = React.useState(
    (window.location.pathname === process.env.PUBLIC_URL ||
      window.location.pathname === process.env.PUBLIC_URL + "/") &&
      location.hash.trim() === "",
  );
  const [statusDialog, setStatusDialog] = React.useState<boolean>(false);
  const [globalChatHistory, setGlobalChatHistory] = React.useState<
    ChatMessage[]
  >([{ sender: "gpt", message: "Hello! How can I help you today?" }]);

  const {
    data: basicSettings,
    isLoading: dashboardLoading,
    refetch: dashboardRefetch,
  } = useQuery(["fetchBasicSettings"], fetchBasicSettings, {
    enabled: false,
  });

  const { data: deviceProperties } = useQuery(["fetchDeviceProperties"], () =>
    fetchDeviceProperties(),
  );

  useQuery(["fetchSelectedLanguage"], () => fetchSelectedLanguage());

  // const { data: anomalies } = useQuery(["gethostlist"], gethostlist);
  const [filteredAnomalies, setFilteredAnomalies] = React.useState<any[]>([]);

  const logoPath =
    theme === "light" ? "/images/ls-logo-white.png" : "/images/linklogo.png";
  const logo = process.env.STATIC_PATH + logoPath;

  const customPath =
    process.env.STATIC_PATH + "/images/uploads/" + deviceProperties?.logoName;

  const { data: recentAlertData } = useQuery(
    ["recentAlerts"],
    () => recentAlerts(),
    {
      refetchInterval: 10000,
    },
  );

  const { data: recentAlertDemoData } = useQuery(
    ["gethostlist"],
    () => gethostlist(),
    {
      refetchInterval: 10000,
    },
  );

  const { data: searchresults, isLoading: searchLoading } = useQuery<{
    entities: any;
    users: any;
    files: any;
  }>(
    ["globalSearch", globalSearch],
    ({ signal }) => getSearchResults(globalSearch, productType, true, signal),
    {
      enabled: !!globalSearch,
    },
  );

  const { data: systemArchitectureInfo } = useQuery(
    ["systemArchitectureInfo"],
    ({ signal }) => getSystemArchitectureInfo(signal),
  );

  const readRecentAlertMutation = useMutation(readRecentAlerts, {
    onSuccess: () => {
      setRecentAlert((prev: any) => {
        return prev.map((alert: any) => {
          alert.read_status = "1";
          return alert;
        });
      });
      setRecentAlertRead(true);
    },
  });

  const readSystemAlertMutation = useMutation(readSystemAlerts, {
    onSuccess: () => {
      setSystemAlerts((prev: any) => {
        return prev.map((alert: any) => {
          alert.read_status = "1";
          return alert;
        });
      });
      setSystemAlertsRead(true);
    },
  });

  const handleSearch = useDebounce((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.trim().length > 2) {
      setAdvancedSearchLoading(true);
      setGlobalSearchResult(undefined);
      setGlobalSearch(e.target.value);
      // setResultView(!!e.target.value);

      //Widgets
      searchEventSource = new EventSource(
        process.env.API_URL +
          "/advancedSearch/?advancedSearchInput=" +
          encodeURIComponent(e.target.value.trim()) +
          "&shadowGPT=false&version=new",
        { withCredentials: true },
      );

      searchEventSource.onmessage = function (event: any) {
        const data = JSON.parse(event.data);
        const widgets = data.widgets;
        const temp: any = [];
        widgets.forEach((widget: any) => {
          temp.push({
            icon: <ChartArea size={16} />,
            title: widget.name,
            parent: widget.parent,
            page: widget.page,
          });
        });
        setAdvancedSearchLoading(false);
        setWidgetTableRows(temp);
        searchEventSource.close();
      };
      // if (anomalies && anomalies.length > 0) {
      //   const anomalyResults = anomalies.filter((anomaly: any) =>
      //     JSON.stringify(anomaly)
      //       .toLowerCase()
      //       .includes(e.target.value.toLowerCase()),
      //   );
      //   setFilteredAnomalies(anomalyResults);
      // }
    } else {
      setAdvancedSearchLoading(false);
      setWidgetTableRows([]);
      setFilteredAnomalies([]);
    }
  }, 500);

  const { data: automationApprovalAlerts } = useQuery(
    ["automationApprovalAlerts"],
    () => getAutomationApprovalAlerts(),
    {
      refetchInterval: 60 * 1000,
    },
  );

  React.useEffect(() => {
    if (automationApprovalAlerts?.data) {
      setSystemAlerts((prev: any) => {
        const nonAutomation = prev.filter(
          (a: any) => a.alertType !== "automation",
        );
        return [
          ...nonAutomation,
          ...automationApprovalAlerts.data.filter(
            (alert: any, index: number, arr: any[]) =>
              arr.findIndex(
                (a) =>
                  a.additionalFields?.ApprovalUID ===
                  alert.additionalFields?.ApprovalUID,
              ) === index,
          ),
        ];
      });
    }
  }, [automationApprovalAlerts]);

  React.useEffect(() => {
    if (isToolChecked) {
      setShadowSearch("__list_tools__");
    }
  }, [isToolChecked]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      event.key === "Enter" &&
      shadowSearchRef.current &&
      shadowGPTStatus == "on"
    ) {
      // Perform your action when "Enter" is pressed
      // setShadowSearch(globalSearch);
      setShadowSearch(shadowSearchRef.current.value);
      setShadowSearchStop(false);
      setChatExpanded(true);
      shadowSearchRef.current.value = "";
    }
  };

  const currentTimeSec = new Date().getTime() - 86400000; //1 Days

  React.useEffect(() => {
    if (recentAlertData) {
      const tempAlerts: any = [];
      recentAlertData
        .sort((a: any, b: any) => b.inserted_time - a.inserted_time)
        .forEach((element: any) => {
          if (parseInt(element.inserted_time) * 1000 > currentTimeSec) {
            tempAlerts.push(element);
          }
        });

      setRecentAlert(tempAlerts);
    }
  }, [recentAlertData]);

  React.useEffect(() => {
    if (recentAlertDemoData) {
      const tempAlerts: any = [];
      recentAlertDemoData
        .sort((a: any, b: any) => b.epochTime - a.epochTime)
        .forEach((element: any) => {
          if (Number(element.epochTime) * 1000 > currentTimeSec) {
            tempAlerts.push(element);
          }
        });

      setRecentDemoAlert(tempAlerts);
    }
  }, [recentAlertDemoData]);

  React.useEffect(() => {
    if (isFeatureAllowed(ALL_KEYS.HEADER_ALERTS)) {
      const eventSource = new EventSource(
        process.env.API_URL + "/systemAlerts/",
      );

      eventSource.onmessage = (event) => {
        const tempAlerts: any = [];
        if (event.data) {
          try {
            const parsedData = JSON.parse(event.data);
            Object.keys(parsedData).forEach((key) => {
              const alertData = JSON.parse(parsedData[key]);
              alertData.forEach((element: any) => {
                if (
                  parseInt(element.lastUpdatedTime) * 1000 > currentTimeSec &&
                  element.alertType !== "automation"
                ) {
                  tempAlerts.push(element);
                }
              });
            });
          } catch (error) {
            console.error("Error parsing system alerts data:", error);
          }
        }

        tempAlerts.sort(
          (a: any, b: any) =>
            parseInt(b.lastUpdatedTime) - parseInt(a.lastUpdatedTime),
        );
        setSystemAlerts((prev: any) => {
          const automationAlerts = prev.filter(
            (a: any) => a.alertType === "automation",
          );
          return [...tempAlerts, ...automationAlerts];
        });
      };

      eventSource.onerror = () => {
        // console.error("EventSource failed:", error);
        eventSource.close();
      };

      return () => {
        eventSource.close();
      };
    }
  }, []);

  React.useEffect(() => {
    if (!searchresults) return;

    setGlobalSearchResult(searchresults);

    const collectAnomalies = (items: any[] = []) =>
      items.flatMap((item) => item?.anomaly_data ?? []);

    const anomaliesTemp = [
      ...collectAnomalies(searchresults.entities || []),
      ...collectAnomalies(searchresults.users || []),
    ];
    setFilteredAnomalies(anomaliesTemp);
  }, [searchresults]);

  React.useEffect(() => {
    if (searchOpen) {
      setTimeout(() => {
        setSearchView(searchOpen);
      }, 100);
    } else {
      setSearchView(searchOpen);
    }
  }, [searchOpen]);

  React.useEffect(() => {
    if (searchView) {
      setTimeout(() => {
        shadowSearchRef?.current?.focus();
      }, 100);
    }
  }, [searchView]);

  React.useEffect(() => {
    return () => {
      setGlobalSearch("");
      setShadowSearch("");
      setGlobalSearchResult(undefined);
      setWidgetTableRows([]);
      setIsListening(false);
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [searchOpen]);

  React.useEffect(() => {
    if ("hash" in location) {
      if (location.hash.includes("#user=")) {
        const user = location.hash.split("#user=");
        if (user?.length > 1) {
          const username = user[1];
          setUserInvestigatorAttrs({ username: username });
        }
      }
    }
  }, [location]);

  function showHome() {
    // if (window.location.pathname == "/") {
    //   // setMapVisible(true);
    // } else {
    // }
    navigate("/");
    setMapVisible(true);
  }

  const widgetTableHeader = [
    { header: "Icon", accessor: "icon", type: "component" },
    { header: "Title", accessor: "title" },
    { header: "Parent", accessor: "parent" },
  ];

  function openResultPage(row: any) {
    const page = row.page;
    const title = row.title;
    setSearchOpen(false);
    setSearchView(false);
    if (page == "ai-engine") {
      navigate("/ai-engine/visual/?widgetName=" + title);
    } else {
      navigate(page + "/?widgetName=" + title);
    }
  }

  function openDashboardList() {
    if (basicSettings) return;
    dashboardRefetch();
  }

  function readAllAlerts(value: string) {
    if (value == "recent") {
      if (!recentAlertRead) {
        readRecentAlertMutation.mutate();
      }
    } else if (value == "system") {
      if (!systemAlertsRead) {
        readSystemAlertMutation.mutate();
      }
    }
  }

  const handleStatusDialog: () => void = () => {
    setStatusDialog(true);
  };

  const handleCloseStatusDialog: () => void = () => {
    setStatusDialog(false);
  };
  const handleGlobalClearChat = () => {
    if (shadowSearchRef.current) shadowSearchRef.current.value = "";
    setShadowSearch("/clear");
    setTimeout(() => {
      setShadowSearch("");
      setGlobalChatHistory([{ sender: "gpt", message: "Hi, I am ShadowGPT" }]);
    }, 200);
  };

  function handleToolToggle() {
    setIsToolChecked(!isToolChecked);
    handleGlobalClearChat();
  }

  const handleDialogClose = () => {
    setSearchOpen(false);
    handleGlobalClearChat();
  };
  function showTools() {
    const defaultTools = shadowGPTdata?.default_tools || [];
    const externalTools = shadowGPTdata?.external_mcp_server_tools || [];

    return (
      <div className="space-y-3 min-w-[220px]">
        {defaultTools.length > 0 && (
          <div>
            <div className="text-xs font-semibold text-muted-foreground mb-1">
              Default Tools
            </div>

            <ul className="space-y-1">
              {defaultTools.map((tool: string, index: number) => (
                <li key={`default-${index}`} className="text-sm">
                  {tool}
                </li>
              ))}
            </ul>
          </div>
        )}

        {externalTools.length > 0 && (
          <div>
            <div className="text-xs font-semibold text-muted-foreground mb-1">
              External MCP Tools
            </div>

            <ul className="space-y-1">
              {externalTools.map((tool: string, index: number) => (
                <li key={`external-${index}`} className="text-sm">
                  {tool}
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="border-t pt-2">
          <p className="text-xs text-muted-foreground leading-relaxed">
            If you want to connect an external MCP server, you can do it from
            Settings page
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <ApprovalPopup />
      <TwoFactorAuth />
      {setupData && setupData.setupWizard == "0" && <SetupWizard />}
      <ReportStatusPopup
        statusDialog={statusDialog}
        handleCloseStatusDialog={handleCloseStatusDialog}
      />
      {mapVisible ? (
        productType == "cmx" ? (
          <MapWheelCMX onClose={() => setMapVisible(false)} />
        ) : (
          <MapWheel onClose={() => setMapVisible(false)} />
        )
      ) : null}
      {macAddress != "" && (
        <>
          {isFeatureAllowed(ALL_KEYS.ASSET_ENTITY_INSPECTOR) && (
            <AssetEntityInspectorContent
              open={true}
              onClose={() => setMacAddress("")}
              mac={macAddress}
            />
          )}
        </>
      )}
      {Object.keys(UserInvestigatorAttrs).length > 0 && (
        <>
          {isFeatureAllowed(ALL_KEYS.USER_INVESTIGATOR_ACCESS) && (
            <UserInvestigator
              account={UserInvestigatorAttrs.username}
              pluginId={UserInvestigatorAttrs.plugin_id}
              openState={true}
              onClose={() => setUserInvestigatorAttrs({})}
            />
          )}
        </>
      )}

      {fileId && (
        <FileInvestigator
          Id={fileId}
          isOpen={true}
          onClose={() => setFileId(null)}
        />
      )}

      {incidentOverviewProps.showIncidentOverview && (
        <IncidentOverview
          openState={incidentOverviewProps.showIncidentOverview}
          onClose={() =>
            setIncidentOverviewProps({
              showIncidentOverview: false,
              incidentProps: {},
            })
          }
          incidentProps={incidentOverviewProps.incidentProps}
        />
      )}

      <header className="bg-primary-background">
        <div className="w-full h-16 flex justify-between text-primary py-5 px-8 relative shadow">
          <div className="basis-64 flex items-center">
            <Link to="/">
              <div className="flex gap-2">
                <div className="overflow-hidden w-60">
                  <img
                    src={logo}
                    alt="LinkShadowLosgo"
                    className="w-full animate-slide_in_left"
                  />
                </div>
                {deviceProperties &&
                  (deviceProperties.logoType || "") == "custom" && (
                    <div className="flex items-center border-l-2 ps-2 overflow-hidden w-80">
                      <img
                        src={customPath}
                        onError={(e: any) => e.target.parentElement.remove()}
                        alt="customLogo"
                        className="object-contain h-10 max-w-80 animate-slide_in_right"
                      />
                    </div>
                  )}
              </div>
            </Link>
          </div>
          <div className="basis-1/2 text-secondary-foreground flex justify-end items-center gap-3">
            {isFeatureAllowed(ALL_KEYS.DISTRIBUTED_SYSTEMS) && <MsspView />}
            <div className="flex items-center border-2 w-1/2 relative">
              <Search className="basis-6 mx-3" size={14} />
              {/* <Popover open={resultView} onOpenChange={setResultView} modal={false}>

                <PopoverTrigger>
                  <div className="basis-6 mx-3 absolute w-[95%] h-2 left-0 bottom-0" />
                </PopoverTrigger>
                <Input
                  type="text"
                  placeholder="Search anything here..."
                  onChange={handleSearch}
                  className="w-full bg-background text-xs p-2"
                />
                <PopoverContent
                  align="center"
                  className="w-full min-w-[22vw] mt-4"
                >
                  

                </PopoverContent>
              </Popover> */}

              <Dialog open={searchView} onOpenChange={handleDialogClose}>
                {/* <DialogTrigger asChild> */}
                <Input
                  type="text"
                  placeholder={t("common.search")}
                  // onChange={handleSearch}
                  onClick={() => setSearchOpen(true)}
                  className={`w-full bg-background text-xs p-2 transition ease-in-out delay-150 ${searchOpen ? "translate-y-[40vh] -translate-x-[30vw]  z-level-1" : ""}`}
                />
                {/* </DialogTrigger> */}
                <DialogContent
                  style={{ maxWidth: 45 / scale + "vw", minWidth: "45vw" }}
                >
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-4">
                      {isFeatureAllowed(ALL_KEYS.SHADOW_GPT_FLOAT_PAGE) &&
                        shadowGPTStatus == "on" && (
                          <img
                            src={
                              process.env.STATIC_PATH +
                              "/img/shadowGPT/Circle.gif"
                            }
                            // onClick={() => setShowChat(!showChat)}
                            alt=""
                            className="w-16 h-16 cursor-pointer rounded-full inline-block shadowgpt-image"
                          />
                        )}
                      <Input
                        id="search-input"
                        type="text"
                        placeholder="Search... (Enter at least 3 letters)"
                        ref={shadowSearchRef}
                        onChange={handleSearch}
                        onKeyDown={handleKeyDown}
                        className="flex-1 bg-background text-xs p-2 me-4"
                      />
                      {isFeatureAllowed(ALL_KEYS.SHADOW_GPT_FLOAT_PAGE) && (
                        <>
                          <button
                            id="voiceSearchBtn"
                            onClick={handleVoiceSearch}
                            className={`relative ml-2 border hover:text-primary hover:border-primary p-2 rounded-full ${isListening ? "text-primary border-primary animate-pulse" : ""}`}
                          >
                            <Mic size={18} className="pointer-events-none" />
                          </button>
                          <TooltipProvider delayDuration={0}>
                            <Tooltip>
                              <TooltipTrigger>
                                <Switch
                                  checked={isToolChecked}
                                  onCheckedChange={() => handleToolToggle()}
                                />
                              </TooltipTrigger>
                              <TooltipContent
                                collisionPadding={60}
                                side={"top"}
                              >
                                <div>{showTools()}</div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <div
                            className={`flex gap-1 ms-2 transition-all duration-500 items-baseline ${isListening ? "opacity-100" : "opacity-0"}`}
                          >
                            <div className="w-1.5 h-1.5 rounded-full bg-white animate-bounce"></div>
                            <div className="w-1.5 h-1.5 rounded-full bg-white animate-bounce delay-100"></div>
                            <div className="w-1.5 h-1.5 rounded-full bg-white animate-bounce delay-200"></div>
                            <div className="text-xs ms-2 font-thin">
                              Listening
                            </div>
                          </div>
                        </>
                      )}
                      {shadowSearch && (
                        <StopCircle
                          className="cursor-pointer"
                          onClick={() => {
                            setShadowSearchStop(true);
                            setShadowSearch("");
                            if (shadowSearchRef.current)
                              shadowSearchRef.current.value = "";
                          }}
                        />
                      )}

                      {globalChatHistory.length > 2 && (
                        <Button
                          className="text-sm px-2 rounded bg-transparant text-primary transition cursor-pointer h-8"
                          onClick={handleGlobalClearChat}
                        >
                          <Eraser
                            size={20}
                            className="cursor-pointer text-primary transition-all"
                          />
                        </Button>
                      )}
                    </DialogTitle>
                    <DialogDescription>
                      {/* Make changes to your profile here. Click save when you're done. */}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="flex gap-2 pe-4 max-h-[80vh] overflow-y-auto">
                    <div
                      className={`flex flex-1 ${chatExpanded ? "w-0 opacity-0" : ""} transition-all duration-300 ease-in-out`}
                    >
                      <Tabs defaultValue="entities" className="w-full">
                        <TabsList className={"grid w-full grid-cols-4"}>
                          <TabsTrigger value="entities">
                            Entities{" "}
                            <Badge className="ml-3">
                              {searchLoading ? (
                                <Loader size={14} className="animate-spin" />
                              ) : (
                                (globalSearchResult &&
                                  globalSearchResult?.entities?.length) ||
                                0
                              )}
                            </Badge>
                          </TabsTrigger>
                          <TabsTrigger value="users">
                            Users{" "}
                            <Badge className="ml-3">
                              {searchLoading ? (
                                <Loader size={14} className="animate-spin" />
                              ) : (
                                (globalSearchResult &&
                                  globalSearchResult?.users?.length) ||
                                0
                              )}
                            </Badge>
                          </TabsTrigger>
                          {isFeatureAllowed(ALL_KEYS.HEADER_SEARCH_FILES) && (
                            <TabsTrigger value="files">
                              Files{" "}
                              <Badge className="ml-3">
                                {searchLoading ? (
                                  <Loader size={14} className="animate-spin" />
                                ) : (
                                  (globalSearchResult &&
                                    globalSearchResult?.files?.length) ||
                                  0
                                )}
                              </Badge>
                            </TabsTrigger>
                          )}
                          {isFeatureAllowed(
                            ALL_KEYS.HEADER_SEARCH_ANOMALIES,
                          ) && (
                            <TabsTrigger value="anomalies">
                              Anomalies{" "}
                              <Badge className="ml-3">
                                {searchLoading ? (
                                  <Loader size={14} className="animate-spin" />
                                ) : (
                                  (filteredAnomalies &&
                                    filteredAnomalies?.length) ||
                                  0
                                )}
                              </Badge>
                            </TabsTrigger>
                          )}
                          {isFeatureAllowed(ALL_KEYS.HEADER_SEARCH_CHARTS) && (
                            <TabsTrigger value="charts">
                              Charts{" "}
                              <Badge className="ml-3">
                                {advancedSearchLoading ? (
                                  <Loader size={14} className="animate-spin" />
                                ) : (
                                  widgetTableRows?.length
                                )}
                              </Badge>
                            </TabsTrigger>
                          )}
                        </TabsList>
                        <TabsContent value="entities" className="mt-4">
                          {globalSearchResult &&
                            globalSearchResult?.entities?.length > 0 &&
                            globalSearchResult?.entities?.map((entity: any) => {
                              const name =
                                entity.hostname || entity.mac_addr || "";
                              if (name) {
                                return (
                                  <>
                                    <div
                                      key={entity.mac_addr}
                                      className="text-xs flex items-center cursor-pointer p-1 hover:text-primary"
                                      onClick={() =>
                                        setMacAddress(entity.mac_addr)
                                      }
                                    >
                                      <div className="pe-4">
                                        {entity.entityType === "Mobile" ? (
                                          <Smartphone size={14} />
                                        ) : (
                                          <Monitor size={14} />
                                        )}
                                      </div>
                                      {entity.hostname
                                        ? entity.hostname
                                        : entity.mac_addr}
                                    </div>
                                    <Separator className="my-2" />
                                  </>
                                );
                              }
                            })}
                        </TabsContent>
                        <TabsContent value="users" className="mt-4">
                          {globalSearchResult &&
                            globalSearchResult?.users?.length > 0 &&
                            globalSearchResult?.users?.map((user: any) => {
                              if (user.username.trim()) {
                                return (
                                  <>
                                    <div
                                      key={user.username}
                                      className="flex text-xs items-center cursor-pointer p-1 hover:text-primary"
                                      onClick={() =>
                                        setUserInvestigatorAttrs({
                                          username: user.username,
                                          plugin_id: user.plugin_name,
                                        })
                                      }
                                    >
                                      <div className="pe-4">
                                        <User size={14} />
                                      </div>
                                      {user.username}
                                    </div>
                                    <Separator className="my-2" />
                                  </>
                                );
                              }
                            })}
                        </TabsContent>
                        {isFeatureAllowed(ALL_KEYS.HEADER_SEARCH_ANOMALIES) && (
                          <TabsContent value="anomalies">
                            {filteredAnomalies &&
                              filteredAnomalies?.length > 0 &&
                              filteredAnomalies?.map((anomaly: any) => {
                                return (
                                  <>
                                    <div
                                      key={anomaly.id}
                                      className="flex items-center cursor-pointer p-1 hover:text-primary"
                                      onClick={() => {
                                        // if (anomaly.anomalyType === "Entity") {
                                        //   setMacAddress(anomaly.macAddr);
                                        // } else if (
                                        //   anomaly.anomalyType === "User"
                                        // ) {
                                        //   setUserInvestigatorAttrs({
                                        //     username: anomaly.Username,
                                        //   });
                                        // }
                                        setIncidentOverviewProps({
                                          showIncidentOverview: true,
                                          incidentProps: anomaly,
                                        });
                                      }}
                                    >
                                      <div className="pe-4">
                                        {/* {anomaly.anomalyType === "Entity" ? ( */}
                                        {anomaly.anomaly_type === "entity" ? (
                                          <Monitor size={14} />
                                        ) : (
                                          <User size={14} />
                                        )}
                                      </div>
                                      <div className="flex items-center gap-2">
                                        {/* <p className="text-xs">{`${anomaly.host || anomaly.Username} (${anomaly.name})`}</p> */}
                                        <p className="text-xs">{`${anomaly.anomalous_hostname || anomaly.username} (${anomaly.rule_name})`}</p>
                                        <div className="text-xs px-2 py-1 inline-block bg-primary rounded-md text-white">
                                          {anomaly.Score}
                                        </div>
                                      </div>
                                    </div>
                                    <Separator className="my-2" />
                                  </>
                                );
                              })}
                          </TabsContent>
                        )}
                        {isFeatureAllowed(ALL_KEYS.HEADER_SEARCH_CHARTS) && (
                          <TabsContent value="charts">
                            <DataTable
                              cols={widgetTableHeader}
                              data={widgetTableRows}
                              showHeader={false}
                              pagination={false}
                              search={false}
                              rowsSelection={false}
                              colFilter={false}
                              border={false}
                              onRowClick={(row: any) => openResultPage(row)}
                            />
                          </TabsContent>
                        )}
                        {isFeatureAllowed(ALL_KEYS.HEADER_SEARCH_FILES) && (
                          <TabsContent value="files" className="mt-4">
                            {globalSearchResult &&
                              globalSearchResult?.files?.length > 0 &&
                              globalSearchResult?.files?.map((file: any) => {
                                return (
                                  <>
                                    <div
                                      key={file.file_id}
                                      className="flex text-xs items-center cursor-pointer p-1 hover:text-primary"
                                      onClick={() => setFileId(file.file_id)}
                                    >
                                      <div className="pe-4">
                                        <Files size={14} />
                                      </div>
                                      {file.file_name}
                                    </div>
                                    <Separator className="my-2" />
                                  </>
                                );
                              })}
                          </TabsContent>
                        )}
                      </Tabs>
                    </div>
                    {isFeatureAllowed(ALL_KEYS.SHADOW_GPT_FLOAT_PAGE) &&
                      shadowGPTStatus == "on" && (
                        <div
                          className={`${chatExpanded ? "w-0" : ""} transition-all duration-300 ease-in-out`}
                        >
                          <Separator orientation="vertical" className="mx-6" />
                        </div>
                      )}
                    {isFeatureAllowed(ALL_KEYS.SHADOW_GPT_FLOAT_PAGE) &&
                      shadowGPTStatus == "on" && (
                        <div
                          className={`${chatExpanded ? "w-full" : "w-1/3"} transition-all overflow-y-auto duration-300 ease-in-out flex flex-col relative`}
                        >
                          <CustomTooltip
                            text={chatExpanded ? "Shrink" : "Expand"}
                          >
                            {chatExpanded ? (
                              <Shrink
                                size={18}
                                className="absolute top-2 end-2 cursor-pointer z-modal-level-3"
                                onClick={() => setChatExpanded(false)}
                              />
                            ) : (
                              <Expand
                                size={18}
                                className="absolute top-2 end-2 cursor-pointer z-modal-level-3"
                                onClick={() => setChatExpanded(true)}
                              />
                            )}
                          </CustomTooltip>
                          <div className="flex-1 overflow-y-auto relative max-h-[50vh]">
                            <ShadowGPT
                              onFinish={() => setShadowSearch("")}
                              className="h-auto [&_#chatResponse]:h-auto"
                              searchString={shadowSearch}
                              externalSearch={true}
                              stop={shadowSearchStop}
                              globalChatHistory={globalChatHistory}
                              setGlobalChatHistory={setGlobalChatHistory}
                              data={isToolChecked ? { page: "__mcp__" } : {}}
                            />
                          </div>
                        </div>
                      )}
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <CustomTooltip text={t("header.home")}>
              <Home
                size={18}
                className="cursor-pointer"
                onClick={() => showHome()}
              />
            </CustomTooltip>

            <Popover
              open={notificationOpen}
              onOpenChange={(open) => {
                setNotificationOpen(open);
                if (open) {
                  readAllAlerts("system");
                }
              }}
            >
              <PopoverTrigger asChild>
                <div>
                  <CustomTooltip text={t("header.notifications")}>
                    <AlertTriangle
                      size={18}
                      className={`cursor-pointer 
                      ${recentAlert.find((r: any) => "read_status" in r == false) || systemAlerts.find((r: any) => "read_status" in r == false) ? "text-primary animate-pulse" : ""}`}
                    />
                  </CustomTooltip>
                </div>
              </PopoverTrigger>
              <PopoverContent className="w-96 max-h-[60vh] overflow-y-auto overflow-x-hidden">
                <Tabs
                  defaultValue="system"
                  onValueChange={(value: string) => readAllAlerts(value)}
                >
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="system" className="text-xs">
                      <Clock2Icon size={12} className="me-2" />
                      <span>{t("header.systemAlerts")}</span>
                    </TabsTrigger>
                    <TabsTrigger value="recent" className="text-xs">
                      <Clock2Icon size={12} className="me-2" />
                      <span>{t("header.recentAlerts")}</span>
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="system">
                    {systemAlerts.length === 0 ? (
                      <div className="text-center text-sm">
                        {t("noSystemAlerts")}
                      </div>
                    ) : (
                      <div className="grid gap-2 break-all">
                        <div className="space-y-2">
                          {systemAlerts.map((alert: any, index: any) => {
                            if (
                              alert.label &&
                              alert.lastUpdatedTime &&
                              alert.lastUpdatedTime != 0
                            ) {
                              return (
                                <Notification
                                  key={index}
                                  title={alert.label}
                                  description={alert.desc}
                                  additionalFields={
                                    "additionalFields" in alert
                                      ? alert.additionalFields
                                      : {}
                                  }
                                  timeSeen={EpochToDateTime(
                                    alert.lastUpdatedTime,
                                  )}
                                  message={
                                    alert.additionalFields &&
                                    "additionalFields" in alert &&
                                    "Message" in alert.additionalFields
                                      ? alert.additionalFields.Message
                                      : ""
                                  }
                                  usage={"usage" in alert ? alert.usage : 0}
                                  onClick={
                                    alert.label ===
                                    "Console Notification - Automation Approval"
                                      ? () => {
                                          const uid =
                                            alert.additionalFields?.ApprovalUID;
                                          if (uid) {
                                            window.open(
                                              `/approval/?uid=${uid}`,
                                              "_blank",
                                            );
                                            setNotificationOpen(false);
                                          }
                                        }
                                      : undefined
                                  }
                                />
                              );
                            } else {
                              <></>;
                            }
                          })}
                        </div>
                      </div>
                    )}
                  </TabsContent>
                  <TabsContent value="recent">
                    {(
                      systemArchitectureInfo?.demo_mode === 1
                        ? recentDemoAlert.length === 0
                        : recentAlert.length === 0
                    ) ? (
                      <div className="text-center text-sm">
                        No recent alerts
                      </div>
                    ) : (
                      <div className="grid gap-2">
                        <div className="space-y-2 cursor-pointer">
                          {systemArchitectureInfo?.demo_mode === 1
                            ? recentDemoAlert.map(
                                (alert: any, index: number) => {
                                  if (alert.name && alert.epochTime) {
                                    return (
                                      <Notification
                                        key={index}
                                        title={alert.name}
                                        timeSeen={EpochToDateTime(
                                          alert.epochTime,
                                        )}
                                        host={alert.hostname}
                                        score={alert.score}
                                        message={
                                          alert.data?.events?.[0]
                                            ?.generated_description || ""
                                        }
                                        onClick={() => {
                                          const gid = alert.id || "";
                                          const tab = "entityAnomalies";
                                          navigate(
                                            `${anomalyRouteBase}?tab=${tab}&filterip=${gid}`,
                                          );
                                          setNotificationOpen(false);
                                        }}
                                      />
                                    );
                                  }
                                  return null;
                                },
                              )
                            : recentAlert.map((alert: any, index: number) => {
                                if (
                                  alert.data.ruleName &&
                                  alert.data.time_str
                                ) {
                                  return (
                                    <Notification
                                      key={index}
                                      title={alert.data.ruleName}
                                      timeSeen={alert.data.time_str}
                                      host={alert.data.hostname}
                                      score={alert.data.score}
                                      message={
                                        alert.additionalFields?.Message || ""
                                      }
                                      onClick={() => {
                                        const gid = alert.data.GlobalID || "";
                                        const tab = "entityAnomalies";
                                        navigate(
                                          `${anomalyRouteBase}?tab=${tab}&filterip=${gid}`,
                                        );
                                        setNotificationOpen(false);
                                      }}
                                    />
                                  );
                                }
                                return null;
                              })}
                        </div>
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </PopoverContent>
            </Popover>

            <Popover>
              <PopoverTrigger asChild>
                <div>
                  <CustomTooltip text={t("header.dashboard")}>
                    <LayoutDashboard
                      onClick={() => openDashboardList()}
                      size={18}
                      className="cursor-pointer"
                    />
                  </CustomTooltip>
                </div>
              </PopoverTrigger>
              <PopoverContent className="w-80 max-h-[60vh] overflow-y-auto">
                <div className="p-2 border-b mb-2 sticky top-0 bg-popover -translate-y-2">
                  <div className="mb-2">{t("header.dashboards")}</div>
                  <div className="text-xs relative">
                    <Input
                      onChange={(e: any) => setDashboardSearch(e.target.value)}
                      className="text-xs h-8 ps-8"
                      placeholder={t("common.search")}
                    />
                    <Search size={12} className="absolute top-2 left-2" />
                  </div>
                </div>
                {dashboardLoading ? (
                  <div>
                    {Array.from({ length: 5 }).map((_, index) => (
                      <div
                        className="bg-secondary h-4 col-span-1 rounded-md mb-4 animate-pulse"
                        key={index}
                      ></div>
                    ))}
                  </div>
                ) : (
                  basicSettings &&
                  basicSettings.customDashboardUrls &&
                  basicSettings.customDashboardUrls.length > 0 &&
                  basicSettings.customDashboardUrls
                    .sort((dash1: any, dash2: any) =>
                      dash1.title
                        .toLowerCase()
                        .localeCompare(dash2.title.toLowerCase()),
                    )
                    .map((dashboard: any) => {
                      if (
                        dashboard.title
                          .toLowerCase()
                          .includes(dashboardSearch.toLowerCase())
                      ) {
                        return (
                          <div key={dashboard.url}>
                            <div className="w-full flex items-center justify-between">
                              <NavLink
                                to={`${window.location.origin}${dashboard.url}`}
                                target="_blank"
                                rel="noreferrer"
                                className="hover:text-primary text-xs p-2"
                              >
                                {dashboard.title}
                              </NavLink>
                            </div>
                          </div>
                        );
                      } else {
                        return null;
                      }
                    })
                )}
              </PopoverContent>
            </Popover>

            {window.location.pathname.includes("ai-engine") && (
              <CustomTooltip text={t("header.reportStatus")}>
                <File size={18} onClick={() => handleStatusDialog()} />
              </CustomTooltip>
            )}

            <CustomTooltip text={t("common.settings")}>
              <a href={window.location.origin + "/settings/"}>
                <Settings size={18} />
              </a>
              {/* <NavLink to={window.location.origin + "/settings/"}>
                <Settings size={18} />
              </NavLink> */}
            </CustomTooltip>

            <CustomTooltip text={t("common.logout")}>
              {/* <a href={window.location.origin + "/signout/"}> */}
              <a onClick={() => setShowLogoutConfirmation(true)}>
                <LogOut className="cursor-pointer" size={18} />
              </a>
            </CustomTooltip>

            <CustomTooltip text="Theme">
              <Button className="hidden" onClick={toggleTheme} size="sm">
                {theme === "light" ? (
                  <Sun size={18} className="text-white" />
                ) : (
                  <Moon size={18} className="text-white" />
                )}
              </Button>
            </CustomTooltip>

            <Button
              variant="outline"
              size="sm"
              className={
                "text-xs " +
                (liveStatus ? "border-primary" : "") +
                (isFeatureAllowed(ALL_KEYS.HEADER_LIVE_STATUS) ? "" : " hidden")
              }
              onClick={() => setLiveStatus(!liveStatus)}
            >
              <span className="h-2 w-2 bg-red-500 rounded-full mr-2" />
              {t("header.liveStatus")}
            </Button>
          </div>
        </div>
        {showLogoutConfirmation && (
          <ConfirmDialog
            text="Are you sure you want to logout?"
            confirmAction={() => {
              window.location.href = window.location.origin + "/signout/";
            }}
            rejectAction={() => setShowLogoutConfirmation(false)}
            show={showLogoutConfirmation}
            closeDialog={() => setShowLogoutConfirmation(false)}
          />
        )}
      </header>
    </>
  );
};

export default Header;
